import type { CampaignAnalyticsGranularity } from "@adpilot/shared";
import {
  type AiManagedGroup,
  type AiManagedGroupAnalyticsResponse,
  AiManagedGroupAnalyticsResponseSchema,
  type AiManagedGroupCampaignSelectionInput,
  AiManagedGroupCampaignSelectionResponseSchema,
  type AiManagedGroupCampaignsInput,
  AiManagedGroupCampaignsInputSchema,
  type AiManagedGroupCreateInput,
  AiManagedGroupCreateInputSchema,
  type AiManagedGroupDetail,
  type AiManagedGroupDetailAnalyticsResponse,
  AiManagedGroupDetailAnalyticsResponseSchema,
  AiManagedGroupDetailResponseSchema,
  type AiManagedGroupDiagnostics,
  type AiManagedGroupDiagnosticsInput,
  AiManagedGroupDiagnosticsInputSchema,
  AiManagedGroupDiagnosticsResponseSchema,
  type AiManagedGroupEligibleCampaignsQuery,
  type AiManagedGroupEligibleCampaignsResponse,
  AiManagedGroupEligibleCampaignsResponseSchema,
  type AiManagedGroupLifecycleInput,
  AiManagedGroupLifecycleInputSchema,
  AiManagedGroupListResponseSchema,
  type AiManagedGroupMemberAiInput,
  AiManagedGroupMemberAiInputSchema,
  type AiManagedGroupMoveCampaignsInput,
  AiManagedGroupMoveCampaignsInputSchema,
  type AiManagedGroupPortfolioOption,
  AiManagedGroupPortfolioOptionsResponseSchema,
  type AiManagedGroupUpdateInput,
  AiManagedGroupUpdateInputSchema,
} from "@adpilot/shared";
import { type ReportingPeriod, reportingPeriodSearchParams } from "@/stores/reporting-period";
import { api } from "./api";
import type { ReportingTimeZone } from "./reporting-time-zone";

export async function fetchManagedGroups(shopId?: string): Promise<AiManagedGroup[]> {
  const response = await api
    .get("ai-managed-groups", {
      ...(shopId ? { headers: { "X-Shop-Id": shopId } } : {}),
    })
    .json<unknown>();
  return AiManagedGroupListResponseSchema.parse(response).data;
}

export interface ManagedGroupAnalyticsFilters {
  search?: string;
  status?: string;
  businessGoal?: string;
  executionMode?: string;
  attention?: "all" | "needed" | "clear";
}

export async function fetchManagedGroupAnalytics(
  range: ReportingPeriod,
  granularity: CampaignAnalyticsGranularity,
  timeZone: ReportingTimeZone,
  shopIds: string[],
  filters: ManagedGroupAnalyticsFilters = {},
): Promise<AiManagedGroupAnalyticsResponse> {
  const response = await api
    .get("ai-managed-groups/analytics", {
      searchParams: {
        ...reportingPeriodSearchParams(range, timeZone),
        granularity,
        ...(shopIds.length ? { shopIds: shopIds.join(",") } : {}),
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.status && filters.status !== "all" ? { status: filters.status } : {}),
        ...(filters.businessGoal && filters.businessGoal !== "all"
          ? { businessGoal: filters.businessGoal }
          : {}),
        ...(filters.executionMode && filters.executionMode !== "all"
          ? { executionMode: filters.executionMode }
          : {}),
        ...(filters.attention && filters.attention !== "all"
          ? { attention: filters.attention }
          : {}),
      },
    })
    .json<{ success: true; data: unknown }>();
  return AiManagedGroupAnalyticsResponseSchema.parse(response.data);
}

export async function fetchManagedGroupDetailAnalytics(
  id: string,
  range: ReportingPeriod,
  granularity: CampaignAnalyticsGranularity,
  timeZone: ReportingTimeZone,
): Promise<AiManagedGroupDetailAnalyticsResponse> {
  const response = await api
    .get(`ai-managed-groups/${id}/analytics`, {
      searchParams: { ...reportingPeriodSearchParams(range, timeZone), granularity },
    })
    .json<{ success: true; data: unknown }>();
  return AiManagedGroupDetailAnalyticsResponseSchema.parse(response.data);
}

export async function addManagedGroupCampaigns(
  id: string,
  values: AiManagedGroupCampaignsInput,
): Promise<AiManagedGroupDetail> {
  const response = await api
    .post(`ai-managed-groups/${id}/campaigns`, {
      json: AiManagedGroupCampaignsInputSchema.parse(values),
    })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function removeManagedGroupCampaigns(
  id: string,
  values: AiManagedGroupCampaignsInput,
): Promise<AiManagedGroupDetail> {
  const response = await api
    .delete(`ai-managed-groups/${id}/campaigns`, {
      json: AiManagedGroupCampaignsInputSchema.parse(values),
    })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function moveManagedGroupCampaigns(
  id: string,
  values: AiManagedGroupMoveCampaignsInput,
): Promise<AiManagedGroupDetail> {
  const response = await api
    .post(`ai-managed-groups/${id}/campaigns/move`, {
      json: AiManagedGroupMoveCampaignsInputSchema.parse(values),
    })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function setManagedGroupCampaignAi(
  id: string,
  campaignId: string,
  values: AiManagedGroupMemberAiInput,
): Promise<AiManagedGroupDetail> {
  const response = await api
    .patch(`ai-managed-groups/${id}/campaigns/${campaignId}`, {
      json: AiManagedGroupMemberAiInputSchema.parse(values),
    })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function updateManagedGroup(
  id: string,
  values: AiManagedGroupUpdateInput,
): Promise<AiManagedGroupDetail> {
  const response = await api
    .patch(`ai-managed-groups/${id}`, { json: AiManagedGroupUpdateInputSchema.parse(values) })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function changeManagedGroupLifecycle(
  id: string,
  values: AiManagedGroupLifecycleInput,
): Promise<AiManagedGroupDetail> {
  const response = await api
    .post(`ai-managed-groups/${id}/lifecycle`, {
      json: AiManagedGroupLifecycleInputSchema.parse(values),
    })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function fetchManagedGroup(id: string): Promise<AiManagedGroupDetail> {
  const response = await api.get(`ai-managed-groups/${id}`).json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}

export async function fetchEligibleManagedGroupCampaigns(
  shopId: string,
  options: Partial<AiManagedGroupEligibleCampaignsQuery> = {},
): Promise<Omit<AiManagedGroupEligibleCampaignsResponse, "success">> {
  const response = await api
    .post("ai-managed-groups/eligible-campaigns/search", {
      headers: { "X-Shop-Id": shopId },
      json: options,
    })
    .json<unknown>();
  const parsed = AiManagedGroupEligibleCampaignsResponseSchema.parse(response);
  return { data: parsed.data, pagination: parsed.pagination };
}

export async function selectAllEligibleManagedGroupCampaigns(
  shopId: string,
  input: AiManagedGroupCampaignSelectionInput,
  signal?: AbortSignal,
) {
  const response = await api
    .post("ai-managed-groups/eligible-campaigns/select-all", {
      headers: { "X-Shop-Id": shopId },
      json: input,
      signal,
    })
    .json<unknown>();
  return AiManagedGroupCampaignSelectionResponseSchema.parse(response).data;
}

export async function fetchManagedGroupPortfolioOptions(
  shopId: string,
): Promise<AiManagedGroupPortfolioOption[]> {
  const response = await api
    .get("ai-managed-groups/portfolio-options", {
      headers: { "X-Shop-Id": shopId },
    })
    .json<unknown>();
  return AiManagedGroupPortfolioOptionsResponseSchema.parse(response).data;
}

export async function fetchManagedGroupDiagnostics(
  shopId: string,
  values: AiManagedGroupDiagnosticsInput,
): Promise<AiManagedGroupDiagnostics> {
  const body = AiManagedGroupDiagnosticsInputSchema.parse(values);
  const response = await api
    .post("ai-managed-groups/diagnostics", {
      headers: { "X-Shop-Id": shopId },
      json: body,
    })
    .json<unknown>();
  return AiManagedGroupDiagnosticsResponseSchema.parse(response).data;
}

export async function createManagedGroup(
  shopId: string,
  values: AiManagedGroupCreateInput,
): Promise<AiManagedGroupDetail> {
  const body = AiManagedGroupCreateInputSchema.parse(values);
  const response = await api
    .post("ai-managed-groups", { json: body, headers: { "X-Shop-Id": shopId } })
    .json<unknown>();
  return AiManagedGroupDetailResponseSchema.parse(response).data;
}
